import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Snackbar,
    Typography,
} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { API_BASE_URL } from "../../config/api";
import { buildShareCardUrl, buildSharePlayerUrl } from "../../pages/Player/playerTabs";
import "./PlayerShareModal.css";

function sharePageOrigin() {
    return window.location.origin;
}

function shareCardOrigin() {
    return API_BASE_URL;
}

function playerShareFileName(player) {
    if (!player) return "player-share.png";
    return `${player.first_name}-${player.last_name}-share`.replace(/\s+/g, "-").toLowerCase() + ".png";
}

function PlayerShareModal({ open, onClose, player, shareState = null, activeTab = "consensus" }) {
    const previewBlobRef = useRef(null);
    const [previewSrc, setPreviewSrc] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [downloading, setDownloading] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(true);
    const [sharing, setSharing] = useState(false);

    const resolvedShareState = shareState || { view: "pre-draft", tab: activeTab };

    const shareUrl = useMemo(() => {
        if (!player?.player_id) return "";
        return buildSharePlayerUrl(player.player_id, resolvedShareState, sharePageOrigin());
    }, [player?.player_id, resolvedShareState]);

    const cardUrl = useMemo(() => {
        if (!player?.player_id) return "";
        return buildShareCardUrl(player.player_id, resolvedShareState, shareCardOrigin());
    }, [player?.player_id, resolvedShareState]);

    useEffect(() => {
        if (!open || !cardUrl) return undefined;

        const abortController = new AbortController();
        let cancelled = false;

        setPreviewLoading(true);
        previewBlobRef.current = null;
        setPreviewSrc((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return "";
        });

        (async () => {
            try {
                const res = await fetch(cardUrl, { signal: abortController.signal });
                if (!res.ok) throw new Error("Failed to fetch image");
                const blob = await res.blob();
                if (cancelled) return;
                previewBlobRef.current = blob;
                setPreviewSrc(URL.createObjectURL(blob));
                setPreviewLoading(false);
            } catch (err) {
                if (!cancelled && err?.name !== "AbortError") {
                    setPreviewLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
            abortController.abort();
        };
    }, [open, cardUrl, player?.player_id]);

    useEffect(
        () => () => {
            if (previewSrc) URL.revokeObjectURL(previewSrc);
        },
        [previewSrc]
    );

    const showSnackbar = (message) => {
        setSnackbarMessage(message);
        setSnackbarOpen(true);
    };

    const downloadPreviewBlob = useCallback(() => {
        const blob = previewBlobRef.current;
        if (!blob) return false;
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = playerShareFileName(player);
        link.click();
        URL.revokeObjectURL(url);
        return true;
    }, [player]);

    const handleDownload = async () => {
        if (!previewBlobRef.current) return;
        setDownloading(true);
        try {
            if (downloadPreviewBlob()) {
                showSnackbar("Image downloaded.");
            } else {
                showSnackbar("Could not download image. Try again.");
            }
        } finally {
            setDownloading(false);
        }
    };

    const handleCopyLink = useCallback(async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            showSnackbar("Share link copied.");
        } catch {
            showSnackbar("Could not copy link.");
        }
    }, [shareUrl]);

    const handleNativeShare = useCallback(async () => {
        if (!player || !shareUrl || !previewBlobRef.current) return;
        const title = `${player.first_name} ${player.last_name} | Hockey Pipeline`;
        const text = `View ${player.first_name} ${player.last_name} on Hockey Pipeline.`;
        const basePayload = { title, text, url: shareUrl };

        setSharing(true);
        try {
            if (navigator.share) {
                const file = new File(
                    [previewBlobRef.current],
                    playerShareFileName(player),
                    { type: previewBlobRef.current.type || "image/png" }
                );
                const payloadWithFile = { ...basePayload, files: [file] };
                if (navigator.canShare?.(payloadWithFile)) {
                    await navigator.share(payloadWithFile);
                    return;
                }
                try {
                    await navigator.share(basePayload);
                    return;
                } catch (err) {
                    if (err?.name === "AbortError") return;
                }
            }
            await handleCopyLink();
        } finally {
            setSharing(false);
        }
    }, [player, shareUrl, handleCopyLink]);

    if (!player) return null;

    const playerName = `${player.first_name} ${player.last_name}`.trim();
    const previewReady = !previewLoading && Boolean(previewSrc);

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                className="player-share-dialog"
                PaperProps={{ className: "player-share-dialog-paper" }}
                aria-labelledby="player-share-dialog-title"
            >
                <DialogTitle id="player-share-dialog-title" className="player-share-dialog-title">
                    Share {playerName}
                </DialogTitle>
                <DialogContent className="player-share-dialog-content">
                    <Typography className="player-share-dialog-lead" variant="body2">
                        {resolvedShareState.view === "post-draft" && resolvedShareState.tab === "nhl-stats"
                            ? resolvedShareState.display === "graph"
                                ? "Share this career stats graph."
                                : "Share this career stats table."
                            : resolvedShareState.view === "post-draft"
                                ? "Share this NHL arrival outlook."
                                : "Share a card with rankings, stats, and profile details."}
                    </Typography>

                    <Box className="player-share-preview-frame">
                        {previewLoading && (
                            <Box
                                className="player-share-preview-loading"
                                aria-live="polite"
                                aria-busy="true"
                            >
                                <CircularProgress
                                    size={36}
                                    thickness={4}
                                    sx={{ color: "var(--brand-strong)" }}
                                />
                                <Typography variant="caption" className="player-share-preview-loading-text">
                                    Generating preview…
                                </Typography>
                            </Box>
                        )}
                        {previewSrc && (
                            <img
                                src={previewSrc}
                                alt={`Share card preview for ${playerName}`}
                                className={
                                    "player-share-preview-img" +
                                    (previewLoading ? " player-share-preview-img--loading" : "")
                                }
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions className="player-share-dialog-actions">
                    <Button
                        onClick={onClose}
                        size="small"
                        variant="outlined"
                        className="player-share-btn-secondary"
                    >
                        Close
                    </Button>
                    <Box className="player-share-dialog-actions-group">
                        <Button
                            onClick={handleDownload}
                            disabled={downloading || !previewReady}
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadRoundedIcon />}
                            className="player-share-btn-secondary"
                        >
                            {downloading ? "Downloading…" : "Download"}
                        </Button>
                        <Button
                            onClick={handleCopyLink}
                            size="small"
                            variant="outlined"
                            startIcon={<LinkRoundedIcon />}
                            className="player-share-btn-secondary"
                        >
                            Copy link
                        </Button>
                        <Button
                            onClick={handleNativeShare}
                            disabled={sharing || !previewReady}
                            size="small"
                            variant="contained"
                            disableElevation
                            startIcon={<ShareOutlinedIcon />}
                            className="player-share-btn-primary"
                        >
                            {sharing ? "Sharing…" : "Share"}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity="success"
                    variant="filled"
                    sx={{
                        width: "100%",
                        backgroundColor: "var(--bg-elevated)",
                        color: "var(--text-primary)",
                        border: "1px solid var(--border-default)",
                        "& .MuiAlert-icon": { color: "var(--brand-strong)" },
                    }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}

export default PlayerShareModal;
